import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import AppBarContext from 'context/AppBarContext';
import useSubscription from 'hooks/payments/useSubscription';
import ChargesHistoryTable from 'components/client-payment/ChargesHistoryTable';
import UpdateCardDialog from 'components/client-payment/UpdateCardDialog';

const ROWS_PER_PAGE = 25;

const cardSx = {
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'hairline.main',
  backgroundColor: 'surface.content',
  backgroundImage: 'none',
  boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow)',
};

/**
 * Página cliente "Historial de cobros" (subcategoría de Pagos): historial COMPLETO
 * paginado de cobros de suscripción + tarjeta guardada con opción de actualizarla.
 */
function ClientChargesHistoryPage() {
  const { t } = useTranslation(['payments']);
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const { fetchMySubscription, fetchMyCharges } = useSubscription();

  const [subscription, setSubscription] = useState(null);
  const [charges, setCharges] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);

  const pageTitle = t('subscription.manage.chargeHistory', { defaultValue: 'Historial de cobros' });

  useEffect(() => {
    setAppBarTitle(pageTitle);
    setAppBarButton(null);
  }, [setAppBarButton, setAppBarTitle, pageTitle]);

  useEffect(() => {
    let active = true;
    fetchMySubscription()
      .then((sub) => { if (active) setSubscription(sub); })
      .catch(() => { if (active) setSubscription(null); });
    return () => { active = false; };
  }, [fetchMySubscription]);

  // Desktop pagina REEMPLAZANDO la página; mobile ("Cargar más") va APILANDO.
  const loadCharges = useCallback(async (targetPage, { append = false } = {}) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const result = await fetchMyCharges({ page: targetPage + 1, perPage: ROWS_PER_PAGE });
      setTotal(result?.total || 0);
      setCharges((prev) => (append ? [...prev, ...(result?.data || [])] : (result?.data || [])));
      setPage(targetPage);
    } catch {
      if (!append) {
        setCharges([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [fetchMyCharges]);

  useEffect(() => { loadCharges(0); }, [loadCharges]);

  const canUpdateCard = subscription?.provider === 'wompi'
    && ['active', 'grace_period', 'pending'].includes(subscription?.status);

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Stack spacing={3}>
        {/* Tarjeta guardada + actualizar (misma vista que el historial, pedido de QA) */}
        {subscription?.card_last4 && (
          <Card sx={cardSx}>
            <CardContent sx={{ p: 3 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1.5}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CreditCardIcon sx={{ color: 'gold.main' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                      {t('subscription.manage.savedCard', { defaultValue: 'Tarjeta guardada' })}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 700 }}>
                      {subscription.card_brand} •••• {subscription.card_last4}
                    </Typography>
                  </Box>
                </Stack>
                {canUpdateCard && (
                  <Button variant="outlined" onClick={() => setCardOpen(true)}
                    sx={{ color: 'text.primary', borderColor: 'hairline.strong', fontWeight: 700, '&:hover': { borderColor: 'text.disabled', background: 'rgba(255,255,255,0.06)' } }}>
                    {t('subscription.manage.updateCard')}
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Historial completo de cobros (paginado en servidor) */}
        <Card sx={cardSx}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <ReceiptLongOutlinedIcon sx={{ color: 'gold.main' }} />
              <Typography variant="h6" fontWeight={800} color="text.primary">{pageTitle}</Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              {t('subscription.manage.chargeHistorySubtitle', {
                defaultValue: 'Todos los cobros de tu suscripción, incluidos los fallidos. Toca una fila para ver el detalle.',
              })}
            </Typography>

            {loading && (
              <Stack alignItems="center" py={4}>
                <CircularProgress size={26} sx={{ color: 'gold.main' }} />
              </Stack>
            )}

            {!loading && charges.length === 0 && (
              <Stack alignItems="center" spacing={1} py={4}>
                <ReceiptLongOutlinedIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('subscription.manage.chargesEmpty', { defaultValue: 'Aún no tienes cobros registrados.' })}
                </Typography>
              </Stack>
            )}

            {!loading && charges.length > 0 && (
              <ChargesHistoryTable
                charges={charges}
                t={t}
                serverSide
                total={total}
                page={page}
                onPageChange={(p) => loadCharges(p)}
                onLoadMore={() => loadCharges(page + 1, { append: true })}
                hasMore={charges.length < total}
                loadingMore={loadingMore}
                rowsPerPage={ROWS_PER_PAGE}
                showPlan
              />
            )}
          </CardContent>
        </Card>
      </Stack>

      <UpdateCardDialog
        open={cardOpen}
        onClose={() => setCardOpen(false)}
        subscription={subscription}
        onUpdated={setSubscription}
      />
    </>
  );
}

export default ClientChargesHistoryPage;
